import axios from "axios";
import { checkError } from "../../utils/automations/checkError";
import { transformFormToApiPayload } from "../../utils/transformers/automationTransformers";

// Use the test API URL as base, fallback to production API
const baseURL = process.env.REACT_APP_API_URL;
const apiKey = process.env.REACT_APP_API_KEY;

export const apiClient = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${apiKey}`,
    InternalSecret: "HardCodedPassword",
  },
});

const withBrand = (brandId) =>
  brandId != null ? { headers: { brandId: String(brandId) } } : {};

// Wrapper that preserves response structure for compatibility
const wrap = async (promise, preserveFullResponse = false) => {
  try {
    const res = await promise;
    // Return full response for compatibility with existing code
    return preserveFullResponse ? res : res.data;
  } catch (err) {
    try {
      checkError(err?.response?.data || err);
    } catch (_) {}
    throw err;
  }
};

// Journey/Automation functions (original API style)
export const createJourneyAutomation = async (payload, brandId, userId) => {
  const apiPayload = transformFormToApiPayload(payload, brandId, userId);
  return wrap(
    apiClient.post(
      "/V1/Journey/CreateJourneyAutomation",
      apiPayload,
      withBrand(brandId)
    )
  );
};

export const editJourneyAutomation = async (payload) => {
  return wrap(
    apiClient.patch(
      "/V1/Journey/EditJourneyAutomation",
      payload,
      withBrand(payload?.BrandId)
    )
  );
};

export const saveJourneyAutomation = async (payload) => {
  return wrap(apiClient.patch("/V1/Journey/SaveJourneyAutomation", payload));
};

export const addJourneyActions = async (payload) => {
  return wrap(apiClient.post("/V1/Journey/AddJourneyActions", payload));
};

export const deleteJourneyActions = async (actionId) => {
  return wrap(apiClient.delete(`/V1/Journey/DeleteJourneyActions/${actionId}`));
};

export const deleteJourneyAutomation = async (automationId) => {
  return wrap(
    apiClient.delete(`/V1/Journey/DeleteJourneyAutomation/${automationId}`)
  );
};

export const getJourneyAutomations = async (brandId) => {
  return wrap(
    apiClient.get("/V1/Journey/GetJourneyAutomations", withBrand(brandId))
  );
};

export const getJourneyAutomation = async (automationId) => {
  return wrap(
    apiClient.get("/V1/Journey/GetJourneyAutomation", {
      headers: {
        ...withBrand(localStorage.getItem("brandId")).headers,
      },
      data: { Id: automationId },
    })
  );
};

// Test API functions - maintain compatibility with existing hooks
export const getUserData = async () => {
  return wrap(apiClient.get("/Journey/GetUserData"), true); // Return full response
};

export const getBrandSegments = async (brandId) => {
  return wrap(
    apiClient.get(`/Values/Segments/${brandId}`, withBrand(brandId)),
    true // Return full response
  );
};

export const getDynamicTables = async (brandId) => {
  return wrap(
    apiClient.get(
      `/Values/DynamicTables?brandId=${brandId}`,
      withBrand(brandId)
    ),
    true // Return full response
  );
};

export const getDynamicTableFields = async (brandId) => {
  try {
    const response = await apiClient.get(
      `/Values/DynamicTableFields/${brandId}`
    );
    return response.data;
  } catch (error) {
    console.error("API call failed:", error.response?.data || error);
    throw error;
  }
};

export const getDynamicTableFieldsById = async (brandId, tableId) => {
  try {
    const response = await apiClient.get(
      `/Values/DynamicTableFields/${brandId}/${tableId}`
    );
    return response.data;
  } catch (error) {
    console.error("API call failed:", error.response?.data || error);
    throw error;
  }
};

// Automation CRUD operations
export const getAutomations = async () => {
  return wrap(apiClient.get("/Automation"));
};

export const getAutomationsBybrandId = async (brandId) => {
  const response = await wrap(
    apiClient.get(`/Automation/automationsof/${brandId}`),
    true // Get full response
  );
  return transformAutomationResponse(response.data);
};

export const createAutomation = async (automationData, brandId, userId) => {
  const payload = transformFormToApiPayload(automationData, brandId, userId);
  return wrap(apiClient.post("/Automation", payload));
};

export const getAutomationById = async (id) => {
  return wrap(apiClient.get(`/Automation/GetAutomation/${id}`));
};

export const updateAutomation = async (id, automationData, brandId, userId) => {
  const payload = {
    ...transformFormToApiPayload(automationData, brandId, userId),
    Id: id,
  };
  return wrap(apiClient.put(`/Automation/${id}`, payload, withBrand(brandId)));
};

export const deleteAutomation = async (id) => {
  return wrap(apiClient.delete(`/Automation/${id}`));
};

// Flow operations
export const getFlowByAutomationId = async (automationId) => {
  const response = await wrap(apiClient.get(`/Flow/${automationId}`), true);
  const item = Array.isArray(response.data) ? response.data[0] : response.data;

  return {
    automationId: item?.AutomationId,
    nodes: JSON.parse(item?.NodesJson || "[]"),
    edges: JSON.parse(item?.EdgesJson || "[]"),
    viewport: JSON.parse(item?.ViewportJson || "{}"),
  };
};

export const createFlow = async (flowData) => {
  const payload = {
    AutomationId: flowData.AutomationId,
    NodesJson: JSON.stringify(flowData.nodes || []),
    EdgesJson: JSON.stringify(flowData.edges || []),
    ViewportJson: JSON.stringify(flowData.viewport || {}),
    EntityKey: null,
  };
  const res = await wrap(apiClient.post("/Flow", payload));
  return {
    AutomationId: res.AutomationId,
    nodes: JSON.parse(res.NodesJson || "[]"),
    edges: JSON.parse(res.EdgesJson || "[]"),
    viewport: JSON.parse(res.ViewportJson || "{}"),
  };
};

export const updateFlow = async (automationId, flowData) => {
  const payload = {
    automationId,
    nodesJson: JSON.stringify(flowData.nodes || []),
    edgesJson: JSON.stringify(flowData.edges || []),
    viewportJson: JSON.stringify(flowData.viewport || {}),
  };
  const res = await wrap(apiClient.put(`/Flow/${automationId}`, payload));
  return {
    automationId: res.automationId,
    nodes: JSON.parse(res.nodesJson || "[]"),
    edges: JSON.parse(res.edgesJson || "[]"),
    viewport: JSON.parse(res.viewportJson || "{}"),
  };
};

export const deleteFlow = async (automationId) => {
  return wrap(apiClient.delete(`/Flow/${automationId}`));
};

// WhatsApp operations
export const getWhatsappNumbers = async (brandId) => {
  return wrap(
    apiClient.get(`/Values/WhatsappNumbers/${brandId}`, withBrand(brandId)),
    true // Return full response
  );
};

export const createWhatsappTemplate = async (brandId, templateData) => {
  const payload = {
    TemplateName: templateData.templateName,
    TemplateContents: templateData.templateContent,
    Parameters: templateData.templateVariables,
    BrandId: Number(brandId),
  };
  return wrap(
    apiClient.post("/SMS/CreateWhatsappTemplate", payload, withBrand(brandId)),
    true // Return full response
  );
};

export const getWhatsappTemplate = async (brandId) => {
  return wrap(
    apiClient.post("/SMS/GetWhatsappTemplates", null, withBrand(brandId)),
    true // Return full response
  );
};

export const getSingleWhatsappTemplate = async (templateId) => {
  const response = await wrap(
    apiClient.get(`/SMS/GetSingleWhatsappTemplate?templateId=${templateId}`),
    true // Return full response
  );
  return response.data.Data; // Maintain original structure
};

// Token operations
export const tryGetToken = async (brandId) => {
  return wrap(
    apiClient.get(
      `/SMS/GetInforuTokenBrandId?brandId=${brandId}`,
      withBrand(brandId)
    ),
    true // Return full response
  );
};

export const createInforUToken = async (
  brandId,
  userId,
  username,
  inforuApiKey
) => {
  return wrap(
    apiClient.post(
      `/SMS/CreateInforUToken?brandId=${brandId}&userId=${userId}&username=${encodeURIComponent(
        username
      )}&apiKey=${encodeURIComponent(inforuApiKey)}`,
      null
    ),
    true // Return full response
  );
};

// SMS operations
export const createSmsSender = async (brandId, sender) => {
  return wrap(
    apiClient.post(
      `/SMS/CreateSmsSender?brandId=${brandId}&sender=${encodeURIComponent(
        sender
      )}`,
      null,
      withBrand(brandId)
    ),
    true // Return full response
  );
};

export const getSmsSenders = async (brandId) => {
  return wrap(
    apiClient.get(`/SMS/GetSmsSenders?brandId=${brandId}`, withBrand(brandId)),
    true // Return full response
  );
};

export const createSmsTemplate = async (brandId, templateData) => {
  const payload = {
    TemplateName: templateData.templateName,
    Content: templateData.templateContent,
    Description: templateData.templateDescription,
    BrandId: Number(brandId),
  };
  return wrap(
    apiClient.post("/SMS/CreateSMSTemplate", payload, withBrand(brandId)),
    true // Return full response
  );
};

export const getSmsTemplates = async (brandId) => {
  return wrap(
    apiClient.get(
      `/SMS/GetSmsTemplates?brandId=${brandId}`,
      withBrand(brandId)
    ),
    true // Return full response
  );
};

// Email operations
export const getEmailSources = async (brandId) => {
  return wrap(
    apiClient.get(`/Values/Emails/${brandId}`, withBrand(brandId)),
    true // Return full response
  );
};

// Campaign operations
export const getCampaigns = async (brandId) => {
  return wrap(
    apiClient.get(`/Values/Campaigns/${brandId}`, withBrand(brandId)),
    true // Return full response
  );
};

// Integration operations
export const getIntegrations = async (brandId, campaignId) => {
  const payload = { BrandId: Number(brandId), CampaignId: campaignId };
  return wrap(
    apiClient.post("/Values/Integrations", payload, withBrand(brandId)),
    true // Return full response
  );
};

// Test operations
export const tesFilter = async () => {
  return wrap(apiClient.get("/Journey/GetApplicableFlowTest"));
};

export const testQueue = async (brandId) => {
  const payload = { BrandId: Number(brandId), LeadsData: [] };
  return wrap(
    apiClient.post(
      `/Journey/SetFlowCommandQueue?brandId=${brandId}`,
      payload,
      withBrand(brandId)
    )
  );
};

// Query operations
export const getQuery = async (brandId, conditions) => {
  const payload = { BrandId: Number(brandId), Conditions: conditions };
  return wrap(
    apiClient.post("/Journey/GetQuery", payload, withBrand(brandId)),
    true // Return full response
  );
};

export const getQueryResults = async (brandId, conditions) => {
  try {
    const payload = { BrandId: Number(brandId), Conditions: conditions };
    const response = await apiClient.post(
      "/Journey/GetQueryResults",
      payload,
      withBrand(brandId)
    );

    // Debug logging
    console.log("Raw API Response:", response);
    console.log("Response data:", response.data);
    console.log("Response structure:", JSON.stringify(response.data, null, 2));

    // Return the full response for now so we can see the structure
    return response;
  } catch (error) {
    console.error(
      "getQueryResults API call failed:",
      error.response?.data || error
    );
    throw error;
  }
};

// UPDATED: Now accepts joinConfigurations parameter
export const executeQueryWithJoinData = async (
  brandId,
  conditions,
  joinConfigurations = null
) => {
  try {
    console.log(conditions);
    console.log(joinConfigurations);

    const payload = {
      BrandId: Number(brandId),
      Conditions: conditions.conditions,
      JoinConfigurations: joinConfigurations, // NEW: Added join configurations
    };
    console.log(payload);
    const response = await apiClient.post(
      "Journey/GetQueryResultsWithJoinData",
      payload
    );
    console.log(response.data);
    return response.data;
  } catch (error) {
    console.error("Error executing query with join data:", error);
    throw error;
  }
};

export const getBrands = async () => {
  try {
    const response = await apiClient.get("Journey/GetBrands");
    return response;
  } catch (error) {
    console.error("getBrands API call failed:", error);
    throw error;
  }
};

export const getBrandById = async (brandId) => {
  try {
    const response = await apiClient.get(
      `Journey/GetBrand/${brandId}`,
      withBrand(brandId)
    );
    return response;
  } catch (error) {
    console.error("getBrandById API call failed:", error);
    throw error;
  }
};

// Helper function for transforming automation responses
const transformAutomationResponse = (apiResponse) => {
  if (!Array.isArray(apiResponse)) return apiResponse;

  return apiResponse.map((item) => ({
    id: item.Id,
    brandId: item.BrandId,
    name: item.Name,
    description: item.Description,
    status: item.Status,
    priority: item.Priority,
    schedule: {
      start: JSON.parse(item.Schedule || "{}").Start,
      end: JSON.parse(item.Schedule || "{}").End,
      lastRun: JSON.parse(item.Schedule || "{}").LastRun,
      nextRun: JSON.parse(item.Schedule || "{}").NextRun,
    },
    metrics: JSON.parse(item.Metrics || "{}"),
    audiences: JSON.parse(item.Audiences || "{}"),
    settings: JSON.parse(item.Settings || "{}"),
    executionsNum: JSON.parse(item.Metrics || "{}").Executions || 0,
    successesNum: JSON.parse(item.Metrics || "{}").Successes || 0,
    failuresNum: JSON.parse(item.Metrics || "{}").Failures || 0,
  }));
};
